'use client';

import { useUser, useFirestore, useCollection } from '@/firebase';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';
import { collection, query, where, orderBy, limit } from 'firebase/firestore';
import { Loader2, FileText, Calendar, User, ArrowRight, Pencil } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useDoc } from '@/firebase';
import { doc } from 'firebase/firestore';
import type { Post } from '@/models/post';
import { getVisibilityLabel, getVisibilityVariant } from '@/models/post';
import { AdminShortcut } from '@/components/admin-shortcut';

interface PostsFeedProps {
  maxPosts?: number;
  showViewAll?: boolean;
}

export default function PostsFeed({ maxPosts = 3, showViewAll = true }: PostsFeedProps) {
  const { user } = useUser();
  const router = useRouter();
  const firestore = useFirestore();

  // Get user profile
  const userProfileRef = useMemo(() => {
    return user ? doc(firestore, 'users', user.uid) : null;
  }, [user, firestore]);

  const { data: userProfile } = useDoc(userProfileRef);

  const isMember = (userProfile as any)?.isMember;
  const isOfficer = (userProfile as any)?.isOfficer;
  const isAdmin = (userProfile as any)?.isAdmin;

  // Build query based on user permissions - only show published posts
  const postsQuery = useMemo(() => {
    const postsRef = collection(firestore, 'posts');
    
    // Officers and admins can see everything except unlisted
    if (isOfficer || isAdmin) {
      return query(
        postsRef,
        where('isDraft', '!=', true),
        where('visibility', 'in', ['public', 'members', 'officers']),
        orderBy('isDraft'),
        orderBy('publishedAt', 'desc'),
        limit(maxPosts)
      );
    }
    
    // Members can see public and members-only posts
    if (isMember) {
      return query(
        postsRef,
        where('isDraft', '!=', true),
        where('visibility', 'in', ['public', 'members']),
        orderBy('isDraft'),
        orderBy('publishedAt', 'desc'),
        limit(maxPosts)
      );
    }
    
    // Non-authenticated users can only see public posts
    return query(
      postsRef,
      where('isDraft', '!=', true),
      where('visibility', '==', 'public'),
      orderBy('isDraft'),
      orderBy('publishedAt', 'desc'),
      limit(maxPosts)
    );
  }, [firestore, isMember, isOfficer, isAdmin, maxPosts]);

  const { data: postsData, loading: postsLoading } = useCollection(postsQuery);

  const posts: Post[] = useMemo(() => {
    if (!postsData) return [];
    return postsData.map((doc: any) => ({
      ...doc,
      createdAt: doc.createdAt?.toDate?.() || doc.createdAt,
      updatedAt: doc.updatedAt?.toDate?.() || doc.updatedAt,
      publishedAt: doc.publishedAt?.toDate?.() || doc.publishedAt,
    })) as Post[];
  }, [postsData]);

  // Format date for display
  const formatDate = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  // Get preview text (first 150 chars of content)
  const getPreview = (content: string) => {
    const plainText = content.replace(/[#*_\[\]()]/g, '').trim();
    return plainText.length > 150 ? plainText.substring(0, 150) + '...' : plainText;
  };

  if (postsLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Latest Updates
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="ml-2">Loading posts...</p>
        </CardContent>
      </Card>
    );
  }

  if (posts.length === 0) {
    return null; // Don't show the card if there are no posts
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Latest Updates
            </CardTitle>
            <CardDescription>Recent news and announcements</CardDescription>
          </div>
          {showViewAll && posts.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => router.push('/posts')}>
              View All
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {posts.map((post) => (
          <div
            key={post.id}
            className="border-b last:border-b-0 pb-4 last:pb-0 space-y-2 cursor-pointer hover:bg-muted/50 -mx-6 px-6 py-3 transition-colors"
            onClick={() => router.push(`/posts/${post.id}`)}
          >
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-lg">{post.title}</h3>
              <div className="flex items-center gap-2 shrink-0">
                {post.visibility !== 'public' && (
                  <Badge variant={getVisibilityVariant(post.visibility)}>
                    {getVisibilityLabel(post.visibility)}
                  </Badge>
                )}
                {/* Stop click bubble so the card's onClick doesn't
                    swallow the navigation to the editor. */}
                <span onClick={(e) => e.stopPropagation()}>
                  <AdminShortcut
                    href={`/admin/posts/${post.id}/edit`}
                    label="Edit post"
                    icon={<Pencil className="h-4 w-4" />}
                    compact
                  />
                </span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {getPreview(post.content)}
            </p>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span>{post.authorName}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>{formatDate(post.publishedAt || post.createdAt)}</span>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
